import {
  Button,
  Icon,
  PropertySalesGallerySection,
  VirtualToursButton,
} from "dash-ui";
import { formatByMask, PROPERTY_PHONE_NUMBER_MASK } from "dash-common";
import {
  GetPropertyByIdQuery,
  SalesGalleryStatusesEnum,
} from "gql/generated.graphql";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

interface PropertySidebarProps {
  property: GetPropertyByIdQuery["getPropertyById"];
  isSeeker?: boolean;
  isRent: boolean;
  fromNow: string;
  onHistoryClick: () => void;
}

function PropertySidebar({
  property,
  isSeeker,
  isRent,
  fromNow,
  onHistoryClick,
}: PropertySidebarProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <div className="hidden md:flex flex-col gap-4 w-64 shrink-0">
      {/* Action Buttons Section */}
      <div className="bg-white shadow rounded-lg p-4 flex flex-col gap-4">
        {/* Add to Portfolio */}
        {!isSeeker && (
          <button
            className="flex items-center bg-sky-600 text-white rounded-lg w-full justify-center py-2 px-4 gap-4 disabled:cursor-not-allowed disabled:filter-grayscale"
            disabled
          >
            <Icon name="folder" />
            <span>Add to Portfolio</span>
          </button>
        )}

        {/* Track Property */}
        <button
          className={
            isSeeker
              ? "flex items-center bg-sky-600 text-white rounded-lg w-full justify-center py-2 px-4 gap-4 disabled:cursor-not-allowed disabled:filter-grayscale"
              : "flex items-center justify-center py-2 px-4 gap-4 rounded-lg border w-full border-sky-600 text-sky-600 disabled:cursor-not-allowed disabled:filter-grayscale"
          }
          disabled
        >
          <Icon name="bookmarkBorder" />
          <span>Track Property</span>
        </button>
      </div>

      {/* Contact Section */}
      <div className="bg-white shadow rounded-lg p-4 flex flex-col gap-3">
        <p className="font-bold">
          {isRent ? "Contact Leasing Office" : "Contact Development"}
        </p>

        {property?.contactPhone?.number && (
          <div className="flex gap-2 text-left items-center mt-1">
            <Icon name="mobile" size={22} color={theme.colors.g20} />
            <span>
              {property.contactPhone.dialCode || ""}{" "}
              {formatByMask(
                property.contactPhone.number || "",
                PROPERTY_PHONE_NUMBER_MASK
              )}
            </span>
          </div>
        )}

        {property?.contactEmail && (
          <Button
            colorVariation="success"
            w="100%"
            icon="send"
            variation="outline"
            onClick={() =>
              (window.location.href = `mailto:${property.contactEmail}`)
            }
          >
            Email
          </Button>
        )}

        <Button
          colorVariation="success"
          w="100%"
          disabled={!property?.website}
          icon="world"
          onClick={() => property?.website && window.open(property.website)}
        >
          Website
        </Button>
      </div>

      {/* Sales Gallery / Leasing Office Section */}
      {property?.salesGalleryStatus &&
        property?.salesGalleryStatus !==
          SalesGalleryStatusesEnum.NoInformation && (
          <div className="bg-white shadow rounded-lg p-4">
            <PropertySalesGallerySection
              salesGalleryStatus={property?.salesGalleryStatus}
              salesGalleryHours={property?.salesGalleryHours}
              salesGalleryAddress={property?.salesGalleryAddress}
              contactEmail={property?.contactEmail}
              contactPhone={property?.contactPhone}
              isRental={isRent}
            />
          </div>
        )}

      {property?.virtualTourLinks && property.virtualTourLinks.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4">
          <div className="mb-3">
            <p className="font-bold">Virtual Tours</p>
          </div>
          <VirtualToursButton virtualTourLinks={property.virtualTourLinks} />
        </div>
      )}

      {/* History Section */}
      <div className="bg-white shadow rounded-lg p-4 flex flex-col gap-4">
        <p className="font-bold">Last Updated</p>

        <p className="flex gap-2">
          <Icon name="autorenew" color={theme.colors.g20} size={22} />
          <span>
            {t("pages:propertyPreviewPage.historyLabel", {
              date: fromNow,
            })}
          </span>
        </p>

        <Button
          colorVariation="success"
          w="100%"
          disabled={!property?.hasChangelog}
          onClick={onHistoryClick}
        >
          History
        </Button>
      </div>
    </div>
  );
}

export default PropertySidebar;
